import { type FC, useState } from 'react';
import {
  getLabelColor,
  useAppointmentLabelsEnabled,
  useGetLabelGroupsQuery,
  useGetLabelsQuery,
} from '@features/appointment-labels';
import { useCan, useOrganizationId } from '@features/user';
import CheckIcon from '@mui/icons-material/Check';
import LabelIcon from '@mui/icons-material/Label';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useCalendarGroupBy } from '../store/hooks';
import type { ViewMode } from '../types';

import { LabelLensEmptyState } from './LabelLensEmptyState';

/** Catalog manager route — practices create/edit/color/group their labels here. */
const LABELS_ROUTE = '/practice/appointment-labels';

/**
 * A label group's identity glyph: a small cluster of its member labels' colors
 * (up to 3 overlapping dots) — since a group itself has no single color.
 */
const GroupColorCluster: FC<{ colors: (string | null | undefined)[] }> = ({ colors }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
    {colors.slice(0, 3).map((color, index) => (
      <Box
        key={index}
        sx={{
          width: 11,
          height: 11,
          borderRadius: '50%',
          bgcolor: getLabelColor(color),
          border: '1.5px solid',
          borderColor: 'background.paper',
          ml: index === 0 ? 0 : '-5px',
        }}
      />
    ))}
  </Box>
);

interface GroupBySelectorProps {
  /**
   * The active calendar view. The lens only groups the day view into lanes; on
   * week/month the button stays visible (and selections still persist) but the
   * menu says the grouping applies to the day view.
   */
  viewMode: ViewMode;
}

/**
 * Day-view "Group by label" lens (GDN-2018). An icon-only trigger (outline tag
 * when off, filled + tinted when a lens is active) opens a single hierarchical
 * menu: each label GROUP is a row (lens = a lane per label) that expands to its
 * member labels (lens = one lane for that label); standalone labels sit at the
 * top level. Selecting the active row again turns the lens off — there is no
 * separate "Off" entry. The "Split lanes by provider" toggle expands each lane
 * into provider sub-columns.
 *
 * The button is always shown when the feature is enabled (respecting the gate),
 * regardless of view or whether the practice has any labels yet. Without labels
 * the menu becomes an educational empty state that either links a manager into
 * the catalog or points other users at their practice admin. On non-day views
 * the menu keeps working but notes that grouping only affects the day view.
 */
const GroupBySelector: FC<GroupBySelectorProps> = ({ viewMode }) => {
  const organizationId = useOrganizationId();
  const labelsEnabled = useAppointmentLabelsEnabled();
  // Server-enforced permission for the label catalog (`manage_appointment_labels`).
  // Drives whether the empty state links into settings or defers to an admin.
  const canManageLabels = useCan('manage_appointment_labels');
  const { appointmentLabels: tracking } = useEventTracking();
  const { data: groupsData } = useGetLabelGroupsQuery(
    { orgId: organizationId ?? '' },
    { skip: !organizationId || !labelsEnabled },
  );
  const { data: labelsData } = useGetLabelsQuery(
    { orgId: organizationId ?? '' },
    { skip: !organizationId || !labelsEnabled },
  );
  const {
    groupByMode,
    groupByLabelGroupId,
    splitLanesByProvider,
    setGroupBy,
    setSplitLanesByProvider,
  } = useCalendarGroupBy();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const groups = groupsData?.items ?? [];
  const labels = labelsData?.items ?? [];
  const standaloneLabels = labels.filter(label => !label.label_group_id);

  // Feature-gated only. Previously the button also hid until a practice had
  // labels; it now stays visible to surface and teach the feature.
  if (!labelsEnabled) return null;

  const isDayView = viewMode === 'day';
  const hasLabels = groups.length > 0 || labels.length > 0;
  const lensSelected = groupByMode !== 'off';
  // The lens only actually groups the calendar on the day view, so only reflect
  // it as "active" (filled/tinted) there — on week/month it's a neutral tag.
  const isApplied = lensSelected && isDayView;

  const currentName =
    groupByMode === 'group'
      ? (groups.find(g => g.label_group_id === groupByLabelGroupId)?.name ?? 'group')
      : groupByMode === 'label'
        ? (labels.find(l => l.label_id === groupByLabelGroupId)?.name ?? 'label')
        : '';

  const tooltipTitle = isApplied ? `Showing: ${currentName}` : 'Group by label';

  const closeMenu = () => setAnchorEl(null);

  // Selecting the active row again clears the lens (no separate "Off" entry).
  const toggleGroupLens = (groupId: string) => {
    const active = groupByMode === 'group' && groupByLabelGroupId === groupId;
    setGroupBy(active ? 'off' : 'group', active ? null : groupId);
    tracking.calendarLensSelected({ lens_type: active ? 'off' : 'group' });
  };
  const toggleLabelLens = (labelId: string) => {
    const active = groupByMode === 'label' && groupByLabelGroupId === labelId;
    setGroupBy(active ? 'off' : 'label', active ? null : labelId);
    tracking.calendarLensSelected({ lens_type: active ? 'off' : 'label' });
  };

  const labelRow = (label: (typeof labels)[number], indented: boolean) => {
    const selected = groupByMode === 'label' && groupByLabelGroupId === label.label_id;
    return (
      <MenuItem
        key={label.label_id}
        selected={selected}
        onClick={() => toggleLabelLens(label.label_id)}
        sx={{ gap: 1, pl: indented ? 4.5 : 2 }}
      >
        <LabelIcon fontSize="small" sx={{ color: getLabelColor(label.color), flexShrink: 0 }} />
        <ListItemText primary={label.name} />
        {selected && <CheckIcon fontSize="small" sx={{ color: 'primary.main' }} />}
      </MenuItem>
    );
  };

  const groupItems = groups.flatMap(group => {
    const groupLabels = labels.filter(label => label.label_group_id === group.label_group_id);
    if (groupLabels.length === 0) return [];
    const selected = groupByMode === 'group' && groupByLabelGroupId === group.label_group_id;
    return [
      <MenuItem
        key={group.label_group_id}
        selected={selected}
        onClick={() => toggleGroupLens(group.label_group_id)}
        sx={{ gap: 1, pl: 2 }}
      >
        <GroupColorCluster colors={groupLabels.map(label => label.color)} />
        <ListItemText primary={group.name} slotProps={{ primary: { sx: { fontWeight: 600 } } }} />
        {selected && <CheckIcon fontSize="small" sx={{ color: 'primary.main' }} />}
      </MenuItem>,
      ...groupLabels.map(label => labelRow(label, true)),
    ];
  });

  const lensMenuItems = [
    // On week/month the lens can't lane anything; keep the menu usable but say so.
    ...(!isDayView
      ? [
          <Box key="day-note" sx={{ px: 2, pt: 1.25, pb: 0.75, maxWidth: 280 }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}>
              Grouping by label applies to the Day view.
            </Typography>
          </Box>,
          <Divider key="day-note-divider" />,
        ]
      : []),
    ...groupItems,
    ...standaloneLabels.map(label => labelRow(label, false)),
    <Divider key="split-divider" />,
    <MenuItem
      key="split-lanes"
      disabled={!lensSelected}
      onClick={() => {
        setSplitLanesByProvider(!splitLanesByProvider);
        tracking.calendarSplitLanesToggled({ enabled: !splitLanesByProvider });
      }}
      sx={{ gap: 1, pl: 2 }}
    >
      <Checkbox
        size="small"
        checked={splitLanesByProvider}
        disabled={!lensSelected}
        tabIndex={-1}
        disableRipple
        sx={{ p: 0 }}
      />
      <ListItemText primary="Split lanes by provider" />
    </MenuItem>,
  ];

  return (
    <>
      <Tooltip title={tooltipTitle} placement="bottom" arrow={false}>
        {/* Plain icon-button styling to match the download + settings buttons;
            active state is conveyed by the filled tag icon + primary tint. */}
        <IconButton
          aria-label="Group by label"
          onClick={event => setAnchorEl(event.currentTarget)}
          size="small"
          sx={{
            color: isApplied ? 'primary.main' : '#535976',
            '&:hover': { backgroundColor: 'rgba(83, 89, 118, 0.08)' },
          }}
        >
          {isApplied ? <LabelIcon fontSize="medium" /> : <LabelOutlinedIcon fontSize="medium" />}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        sx={{ '& .MuiPaper-root': { minWidth: 240, maxHeight: 420 } }}
      >
        {hasLabels
          ? lensMenuItems
          : [
              <LabelLensEmptyState
                key="empty-state"
                canManage={canManageLabels}
                labelsRoute={LABELS_ROUTE}
                // The catalog page fires `labels_catalog_opened` on mount, so
                // the navigation is already tracked at its destination.
                onSetUpLabels={closeMenu}
              />,
            ]}
      </Menu>
    </>
  );
};

export default GroupBySelector;
